import React, { useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Selection,
} from '@syncfusion/ej2-react-grids';

import type { RegistrationType } from '../../../../../services/RegistrationService/types/registrationTypes';
import { useRegistrationNavigation } from './hooks';
import { DateOfBirthTemplate, formatName } from './templates';

// Grid settings
const gridSettings = {
  allowPaging: true,
  pageSettings: { pageSize: 5 },
  selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
};

export interface RegistrationLookupResultsGridProps {
  registrations: RegistrationType[];
  isLoading: boolean;
  onSelectRegistration: (registration: RegistrationType) => void;
  /** Optional ID for testing */
  testId?: string;
}

/**
 * RegistrationLookupResultsGrid - Displays patient search results
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for navigation
 * - Clean component structure
 */
export const RegistrationLookupResultsGrid: React.FC<RegistrationLookupResultsGridProps> = ({
  registrations,
  isLoading,
  onSelectRegistration,
  testId,
}) => {
  // Use custom hook for navigation
  const { handleRowSelection } = useRegistrationNavigation(registrations, onSelectRegistration);

  // Transform data for grid display
  const gridData = useMemo(
    () =>
      registrations.map((reg) => ({
        id: reg.id,
        mrn: reg.identifiers?.mrn || 'N/A',
        name: formatName(reg.name?.first, reg.name?.middle, reg.name?.last),
        firstName: reg.name?.first || '',
        lastName: reg.name?.last || '',
        gender: reg.demographics?.gender || 'N/A',
        phone: reg.contactInfo?.phone || 'N/A',
        // Don't store any date-like fields that Syncfusion might try to auto-format
      })),
    [registrations]
  );

  // Create date of birth template
  const dateOfBirthTemplate = useMemo(
    () => (props: any) => {
      const registration = registrations.find((reg) => reg.id === props.id);
      return <DateOfBirthTemplate registration={registration} />;
    },
    [registrations]
  );

  if (isLoading) {
    return null; // Loading state handled by parent
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
        Found {registrations.length} patient(s)
      </Typography>

      {registrations.length === 0 ? (
        <Alert severity="info">
          No patients found matching your search criteria.
        </Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height={300}
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="mrn"
                headerText="MRN"
                width="120"
                textAlign="Center"
              />
              <ColumnDirective
                field="name"
                headerText="Patient Name"
                width="200"
              />
              <ColumnDirective
                headerText="Date of Birth"
                width="130"
                textAlign="Center"
                template={dateOfBirthTemplate}
              />
              <ColumnDirective
                field="gender"
                headerText="Gender"
                width="100"
                textAlign="Center"
              />
              <ColumnDirective
                field="phone"
                headerText="Phone"
                width="140"
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection]} />
          </GridComponent>
        </Box>
      )}
    </Box>
  );
};

export default RegistrationLookupResultsGrid;
